import React, { useState, useEffect } from 'react';
import { MessageSquare, Clock, Shield, CreditCard, ArrowRight, Stethoscope, Heart, Pill } from 'lucide-react';

const colors = {
  primary: '#2055df',
  primaryDark: '#1a44b8',
  secondary: '#0f172a',
  accent: '#06b6d4',
  success: '#10b981',
  bg: '#f8fafc',
  white: '#ffffff',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray400: '#94a3b8',
  gray600: '#475569',
  gray700: '#334155',
  gray800: '#1e293b',
  gray900: '#0f172a'
};

// Full section component for home page
export const TelehealthSection = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const services = [
    { icon: <Pill size={isMobile ? 20 : 24} />, title: 'UTI Treatment', desc: 'Quick diagnosis & prescription', price: '$39.99' },
    { icon: <Heart size={isMobile ? 20 : 24} />, title: 'ED Treatment', desc: 'Discreet online consultation', price: '$39.99' },
    { icon: <Shield size={isMobile ? 20 : 24} />, title: 'Birth Control', desc: 'Convenient Rx renewal', price: '$39.99' },
    { icon: <Stethoscope size={isMobile ? 20 : 24} />, title: 'General Care', desc: 'Talk to a doctor today', price: '$39.99' }
  ];

  const benefits = [
    { icon: <MessageSquare size={16} />, text: 'Text consultations' },
    { icon: <Clock size={16} />, text: 'Available 7 days a week' },
    { icon: <Shield size={16} />, text: 'Licensed physicians' },
    { icon: <CreditCard size={16} />, text: 'Affordable flat rate' }
  ];

  return (
    <section style={{ 
      background: colors.gray100, 
      padding: isMobile ? '40px 16px' : '80px 24px' 
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 24 : 48 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            background: `${colors.primary}15`,
            borderRadius: 50,
            marginBottom: 12
          }}>
            <MessageSquare size={14} style={{ color: colors.primary }} />
            <span style={{ color: colors.primary, fontSize: 12, fontWeight: 600 }}>TELEHEALTH SERVICES</span>
          </div>
          <h2 style={{ 
            fontSize: isMobile ? 24 : 36, 
            fontWeight: 700, 
            color: colors.gray900, 
            marginBottom: 12 
          }}>
            Need a Prescription?
          </h2>
          <p style={{ 
            fontSize: isMobile ? 14 : 18, 
            color: colors.gray600, 
            maxWidth: 600, 
            margin: '0 auto' 
          }}>
            Connect with licensed doctors online and get prescriptions sent to your pharmacy
          </p>
        </div>

        {/* Services Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(240px, 1fr))', 
          gap: isMobile ? 12 : 20, 
          marginBottom: isMobile ? 24 : 40 
        }}>
          {services.map((service, i) => (
            <a
              key={i}
              href="https://chatwithdr.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'white',
                padding: isMobile ? 16 : 24,
                borderRadius: 12,
                border: `1px solid ${colors.gray200}`,
                textDecoration: 'none',
                display: 'block'
              }}
            >
              <div style={{
                width: isMobile ? 40 : 48,
                height: isMobile ? 40 : 48,
                background: `${colors.primary}10`,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.primary,
                marginBottom: 12
              }}>
                {service.icon}
              </div>
              <h3 style={{ 
                fontSize: isMobile ? 14 : 18, 
                fontWeight: 600, 
                color: colors.gray900, 
                marginBottom: 4 
              }}>
                {service.title}
              </h3>
              <p style={{ 
                fontSize: isMobile ? 11 : 14, 
                color: colors.gray500, 
                marginBottom: 8,
                lineHeight: 1.4
              }}>
                {service.desc}
              </p>
              <div style={{ 
                fontSize: isMobile ? 16 : 20, 
                fontWeight: 700, 
                color: colors.success 
              }}>
                {service.price}
              </div>
            </a>
          ))}
        </div>

        {/* Benefits */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: isMobile ? 10 : 16, 
          marginBottom: isMobile ? 24 : 32 
        }}>
          {benefits.map((b, i) => (
            <div key={i} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 8,
              padding: isMobile ? 10 : 12,
              background: 'white',
              borderRadius: 8,
              border: `1px solid ${colors.gray200}`
            }}>
              <div style={{ color: colors.primary, flexShrink: 0 }}>{b.icon}</div>
              <span style={{ 
                fontSize: isMobile ? 11 : 14, 
                color: colors.gray700, 
                fontWeight: 500 
              }}>
                {b.text}
              </span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div style={{ textAlign: 'center' }}>
          <a
            href="https://chatwithdr.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: isMobile ? '14px 24px' : '16px 32px',
              background: colors.primary,
              color: 'white',
              borderRadius: 10,
              fontSize: isMobile ? 14 : 16,
              fontWeight: 600,
              textDecoration: 'none',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '100%' : 320,
              boxSizing: 'border-box'
            }}
          >
            <MessageSquare size={18} />
            Start Consultation Now
            <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Banner component for drug prices page - FULLY MOBILE RESPONSIVE
export const TelehealthBanner = ({ drugName }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      marginTop: isMobile ? 24 : 48,
      padding: isMobile ? 20 : 32,
      background: colors.gray100,
      borderRadius: 16,
      border: `1px solid ${colors.gray200}`
    }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: isMobile ? 16 : 20,
        alignItems: 'center',
        textAlign: 'center'
      }}>
        {/* Icon */}
        <div style={{
          width: isMobile ? 48 : 56,
          height: isMobile ? 48 : 56,
          background: `${colors.primary}15`,
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.primary
        }}>
          <Stethoscope size={isMobile ? 24 : 28} />
        </div>

        {/* Text Content */}
        <div>
          <h3 style={{ 
            fontSize: isMobile ? 18 : 24, 
            fontWeight: 700, 
            color: colors.gray900, 
            marginBottom: 8 
          }}>
            Need a Prescription{drugName ? ` for ${drugName}` : ''}?
          </h3>
          <p style={{ 
            fontSize: isMobile ? 13 : 16, 
            color: colors.gray600, 
            marginBottom: 12,
            lineHeight: 1.5,
            maxWidth: 500,
            margin: '0 auto 12px'
          }}>
            Connect with a licensed doctor online for just <strong style={{ color: colors.success }}>$39.99</strong>. 
            Get diagnosed and prescribed in minutes.
          </p>
          
          {/* Benefits Row */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: isMobile ? 8 : 16,
            marginBottom: 16
          }}>
            {[
              { icon: <Clock size={12} />, text: '15-min consult' },
              { icon: <Shield size={12} />, text: 'Licensed MDs' },
              { icon: <Pill size={12} />, text: 'Rx sent to pharmacy' }
            ].map((item, i) => (
              <div key={i} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 4,
                fontSize: isMobile ? 11 : 13,
                color: colors.gray600
              }}>
                <span style={{ color: colors.primary }}>{item.icon}</span>
                {item.text}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button - FULL WIDTH ON MOBILE */}
        <a
          href="https://chatwithdr.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: isMobile ? '14px 24px' : '14px 28px',
            background: colors.primary,
            color: 'white',
            borderRadius: 10,
            fontSize: isMobile ? 14 : 15,
            fontWeight: 600,
            textDecoration: 'none',
            width: isMobile ? '100%' : 'auto',
            boxSizing: 'border-box'
          }}
        >
          <MessageSquare size={16} />
          Start Consultation Now
          <ArrowRight size={14} />
        </a>
      </div>
    </div>
  );
};

// Compact inline component for pharmacy cards
export const TelehealthInlinePromo = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <a
      href="https://chatwithdr.com"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        gap: isMobile ? 10 : 12,
        padding: isMobile ? 14 : 16,
        background: `${colors.primary}08`,
        borderRadius: 10,
        border: `1px solid ${colors.primary}20`,
        textDecoration: 'none',
        marginTop: 16
      }}
    >
      <div style={{
        width: 36,
        height: 36,
        background: `${colors.primary}15`,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.primary,
        flexShrink: 0
      }}>
        <MessageSquare size={18} />
      </div>
      <div style={{ 
        flex: 1, 
        textAlign: isMobile ? 'center' : 'left' 
      }}>
        <p style={{ 
          fontSize: 13, 
          fontWeight: 600, 
          color: colors.gray800, 
          marginBottom: 2 
        }}>
          Need a prescription? Chat with a doctor online
        </p>
        <p style={{ 
          fontSize: 11, 
          color: colors.gray500 
        }}>
          $39.99 consultations available now
        </p>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        color: colors.primary,
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap'
      }}>
        Start Now <ArrowRight size={14} />
      </div>
    </a>
  );
};

// Mini promo for digital card page
export const TelehealthMiniPromo = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <a
      href="https://chatwithdr.com"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        gap: 12,
        padding: isMobile ? 16 : 20,
        background: `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.accent}08 100%)`,
        borderRadius: 12,
        border: `1px solid ${colors.primary}15`,
        textDecoration: 'none'
      }}
    >
      <div style={{
        width: 44,
        height: 44,
        background: `${colors.primary}15`,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.primary,
        flexShrink: 0
      }}>
        <Stethoscope size={22} />
      </div>
      <div style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
        <p style={{ 
          fontSize: 14, 
          fontWeight: 600, 
          color: colors.gray800, 
          marginBottom: 4 
        }}>
          Need a Prescription?
        </p>
        <p style={{ 
          fontSize: 12, 
          color: colors.gray600,
          lineHeight: 1.4
        }}>
          Chat with a licensed doctor online for just <strong style={{ color: colors.success }}>$39.99</strong>
        </p>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '10px 16px',
        background: colors.primary,
        color: 'white',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap'
      }}>
        <MessageSquare size={14} />
        Start Chat
      </div>
    </a>
  );
};

export default TelehealthSection;